import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { isUserRole, rolesAssignableBy } from "./role-options";
import type { UpdateUserRequest, UserListItem } from "../types";
import type { UserRole } from "@/types/api";

interface EditUserDialogProps {
  open: boolean;
  user: UserListItem;
  onClose: () => void;
  onSubmit: (data: UpdateUserRequest) => Promise<void> | void;
  isLoading?: boolean;
  error?: string | null;
}

export function EditUserDialog({
  open,
  user,
  onClose,
  onSubmit,
  isLoading = false,
  error,
}: EditUserDialogProps) {
  const { user: currentUser } = useAuth();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [role, setRole] = useState<UserRole>(user.role);

  const availableRoles = rolesAssignableBy(currentUser?.role);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({ firstName, lastName, role });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit user"
      description={user.email}
      testId="edit-user-dialog"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs"
          >
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="eu-first">First name</Label>
            <Input
              id="eu-first"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="first-name-input"
            />
          </div>
          <div>
            <Label htmlFor="eu-last">Last name</Label>
            <Input
              id="eu-last"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              data-testid="last-name-input"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="eu-role">Role</Label>
          <select
            id="eu-role"
            value={role}
            onChange={(e) => {
              if (isUserRole(e.target.value)) setRole(e.target.value);
            }}
            data-testid="role-select"
            className="w-full bg-page border border-border-subtle rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          >
            {availableRoles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            data-testid="submit-button"
          >
            Save changes
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
